import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import PrimaryButton from "../components/PrimaryButton";
import { MobileResponsive } from "./MobileResponsive";
import { convertToMinutes } from "../utils/convertToMins";
import { deliveryHistoryHeader } from "../data/tableData";
import TernaryButton from "../components/TernaryButton";
import TimeInput from "../components/TimeInput";
import useDataFetch from "../hooks/useDataFetch";
import { useDispatch } from "react-redux";
import {
  deleteDriverService,
  enableDriverService,
  shiftScheduleService,
  suspendDriverService,
  updateDriverService,
  updatePointsService,
} from "../features/driverSlice";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../api/services/firebase";
import toast from "react-hot-toast";
import DriverDeliveryHistoryTable from "../components/DriverDeliveryHistoryTable";
import { convertTo12Hour, convertTo24Hour } from "../utils/convertTime";
import FloatingInput from "../components/FloatingInput";
import { otpService } from "../features/authSlice";
import { Map, useMap } from "@vis.gl/react-google-maps";
import { MapMarker } from "../components/MapMarker";
import { InfoWindowPopup } from "../components/InfoWindow";
import { getEntityData } from "../utils/mapUtils";
import DynamicMetaTags from "../components/DynamicMetaTags";
import Avatar from "react-avatar";
import { fetchOrdersApi } from "../api/services/orderService";

const MapPanner = ({ driverLocation }) => {
  const map = useMap();
  useEffect(() => {
    if (!map || !driverLocation) return;
    map.panTo(driverLocation);
    map.setZoom(14);
  }, [map, driverLocation]);
  return null;
};

const DriverDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, error, isLoading, isValidating, mutate } = useDataFetch(
    `/admin/driver-detail/${id}`
  );

  const {
    data: user,
    error: userError,
    isLoading: isLoading2,
  } = useDataFetch(`/admin/profile`);

  const [driverLocation, setDriverLocation] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    fetchOrdersApi({ driverId: id, page: 1 }).then((res) => {
      setRecentOrders(res.orders || []);
    }).catch(() => setRecentOrders([]));
  }, [id]);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, process.env.REACT_APP_COLLECTION_DRIVER, id), (docSnap) => {
      if (docSnap.exists()) {
        const { coordinatesX, coordinatesY } = docSnap.data();
        if (coordinatesX && coordinatesY) {
          setDriverLocation({
            lat: parseFloat(coordinatesX),
            lng: parseFloat(coordinatesY),
          });
        }
      }
    });
    return () => unsub();
  }, [id]);

  const mapDriver = driverLocation
    ? [{ id, coordinatesX: driverLocation.lat, coordinatesY: driverLocation.lng }]
    : [];

  const [shiftTimings, setShiftTimings] = useState({
    MON: ["09:00 AM", "11:30 PM"],
    TUE: ["09:00 AM", "11:30 PM"],
    WED: ["09:00 AM", "11:30 PM"],
    THU: ["09:00 AM", "11:30 PM"],
    FRI: ["09:00 AM", "11:30 PM"],
    SAT: ["11:00 AM", "05:30 PM"],
    SUN: ["11:00 AM", "05:30 PM"],
  });

  const [disabledST, setDisabledST] = useState(true);
  const [highlightedTimes, setHighlightedTimes] = useState({});
  const [otp, setOtp] = useState("");

  const [sidebars, setSidebars] = useState({
    shiftTimings: false,
    suspendDriver: false,
    editPoints: false,
    cancelTrip: false,
    deleteDriver: false,
    editDetails: false,
  });

  const [editForm, setEditForm] = useState({
    name: "",
    phone: "",
    address: "",
    is_shift_driver: null,
    registrationNumber: "",
    vehicle_type: "",
    vehicle_details: "",
  });
  const [editSaving, setEditSaving] = useState(false);

  const [deleteDriver, setDeleteDriver] = useState({
    deleteDriver: false,
  });

  const [cancelTrip, setCancelTrip] = useState(false);
  const [nonShiftModal, setNonShiftModal] = useState(false);
  const [points, setPoints] = useState(0);
  const [shiftErrors, setShiftErrors] = useState({});
  const [errors, setErrors] = useState({
    otp: "",
  });

  const [suspendDriver, setSuspendDriver] = useState({
    suspendDriver: (!isLoading && data?.data?.is_suspended) || false,
    suspendDriverConfirm: (!isLoading && data?.data?.is_suspended) || false,
    enableSuspendedDriver: (!isLoading && data?.data?.is_suspended) || false,
  });

  const sidebarRefs = {
    shiftTimings: useRef(null),
    suspendDriver: useRef(null),
    editPoints: useRef(null),
    cancelTrip: useRef(null),
    deleteDriver: useRef(null),
    editDetails: useRef(null),
  };

  useEffect(() => {
    if (data && data?.data?.shift_schedule) {
      const updatedTimings = {};

      data?.data?.shift_schedule.forEach((schedule, index) => {
        const dayNames = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

        // Only convert valid times, otherwise assign an empty string
        const startTime =
          schedule?.start_time && schedule?.start_time !== "Invalid date"
            ? convertTo12Hour(schedule?.start_time)
            : "";

        const endTime =
          schedule?.end_time && schedule?.end_time !== "Invalid date"
            ? convertTo12Hour(schedule?.end_time)
            : "";

        // If both start and end times are empty, set empty string for that day
        updatedTimings[dayNames[index]] =
          startTime === "" && endTime === "" ? ["", ""] : [startTime, endTime];
      });

      setShiftTimings(updatedTimings);
      setPoints(data?.data?.points);
    }
  }, [data]);

  /* Google Maps */
  const [selectedMarker, setSelectedMarker] = useState({
    selectedMarker: null,
    entityData: [],
  });
  const [infoWindowShown, setInfoWindowShown] = useState(false);

  const handleMarkerClick = (id, marker, entityType) => {
    setSelectedMarker(() => ({
      selectedMarker: marker,
      entityData: getEntityData(id, entityType, data?.data),
    }));
    setInfoWindowShown(true);
  };

  const handleCloseInfoWindow = () => {
    setInfoWindowShown(false);
    setSelectedMarker({
      selectedMarker: null,
      entityData: null,
    });
  };

  const onMapClick = useCallback(() => {
    setSelectedMarker({
      selectedMarker: null,
      entityData: [],
    });
    setInfoWindowShown(false);
  }, []);

  useEffect(() => {
    if (
      selectedMarker.entityData?.length > 0 &&
      selectedMarker.selectedMarker
    ) {
      setInfoWindowShown(true);
    }
  }, [selectedMarker]);

  // Handle OTP validation
  const handleOtpBlur = useCallback(() => {
    if (!otp || otp.length !== 5) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        otp: "OTP should be 5 digits long",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, otp: "" }));
    }
  }, [otp]);

  // Handle Increment Points
  const handleIncrement = () => {
    setPoints(() => {
      const currentPoints = parseInt(points, 10);
      return currentPoints + 10;
    });
  };

  // Handle Decrement Points
  const handleDecrement = () => {
    setPoints(() => {
      const currentPoints = parseInt(points, 10);
      return Math.max(currentPoints - 10, 0);
    });
  };

  const handleEditTime = (day, index, newTime) => {
    setShiftTimings((prevTimings) => {
      const newTimings = { ...prevTimings };

      // Check for "No shift" input
      newTimings[day][index] = newTime === "No shift" ? "" : newTime;

      validateShiftTimings(newTimings, index, day);
      return newTimings;
    });
  };

  const validateShiftTimings = (timings, index, day) => {
    const startTimeInMinutes = convertToMinutes(timings[day][0]);
    const endTimeInMinutes = convertToMinutes(timings[day][1]);
    const newErrors = { ...shiftErrors };

    if (endTimeInMinutes <= startTimeInMinutes && timings[day][1] !== "") {
      newErrors[day] = "End time must be later than start time.";
      setHighlightedTimes((prev) => ({
        ...prev,
        [day]: index,
      }));
    } else if (
      (timings[day][0] === "" && timings[day][1] !== "") ||
      (timings[day][0] !== "" && timings[day][1] === "")
    ) {
      newErrors[day] = "Both start and end times must be filled or both empty.";
      setHighlightedTimes((prev) => ({
        ...prev,
        [day]: index,
      }));
    } else {
      delete newErrors[day]; // Remove error if timing is valid
      setHighlightedTimes((prev) => {
        const { [day]: _, ...rest } = prev;
        return rest;
      });
    }

    setShiftErrors(newErrors);
  };

  const handleConfirmShift = async () => {
    if (Object.keys(shiftErrors).length === 0) {
      setDisabledST(!disabledST);
      if (!disabledST) {
        const resultAction = await dispatch(
          shiftScheduleService({
            driverID: id,
            newSchedule: [
              {
                day: "Monday",
                start_time: shiftTimings.MON[0]
                  ? convertTo24Hour(shiftTimings.MON[0])
                  : "",
                end_time: shiftTimings.MON[1]
                  ? convertTo24Hour(shiftTimings.MON[1])
                  : "",
              },
              {
                day: "Tuesday",
                start_time: shiftTimings.TUE[0]
                  ? convertTo24Hour(shiftTimings.TUE[0])
                  : "",
                end_time: shiftTimings.TUE[1]
                  ? convertTo24Hour(shiftTimings.TUE[1])
                  : "",
              },
              {
                day: "Wednesday",
                start_time: shiftTimings.WED[0]
                  ? convertTo24Hour(shiftTimings.WED[0])
                  : "",
                end_time: shiftTimings.WED[1]
                  ? convertTo24Hour(shiftTimings.WED[1])
                  : "",
              },
              {
                day: "Thursday",
                start_time: shiftTimings.THU[0]
                  ? convertTo24Hour(shiftTimings.THU[0])
                  : "",
                end_time: shiftTimings.THU[1]
                  ? convertTo24Hour(shiftTimings.THU[1])
                  : "",
              },
              {
                day: "Friday",
                start_time: shiftTimings.FRI[0]
                  ? convertTo24Hour(shiftTimings.FRI[0])
                  : "",
                end_time: shiftTimings.FRI[1]
                  ? convertTo24Hour(shiftTimings.FRI[1])
                  : "",
              },
              {
                day: "Saturday",
                start_time: shiftTimings.SAT[0]
                  ? convertTo24Hour(shiftTimings.SAT[0])
                  : "",
                end_time: shiftTimings.SAT[1]
                  ? convertTo24Hour(shiftTimings.SAT[1])
                  : "",
              },
              {
                day: "Sunday",
                start_time: shiftTimings.SUN[0]
                  ? convertTo24Hour(shiftTimings.SUN[0])
                  : "",
                end_time: shiftTimings.SUN[1]
                  ? convertTo24Hour(shiftTimings.SUN[1])
                  : "",
              },
            ],
          })
        );

        if (shiftScheduleService.fulfilled.match(resultAction)) {
          toast.success(resultAction.payload.message);
        }
      }
    }
  };

  const toggleSidebar = (key, value) => {
    setSidebars((prev) => ({ ...prev, [key]: value }));
  };

  const handleCloseSidebar = () => {
    if (Object.keys(shiftErrors).length === 0) {
      setSidebars({
        editPoints: false,
        shiftTimings: false,
        suspendDriver: false,
        cancelTrip: false,
        deleteDriver: false,
        editDetails: false,
      });
    } else {
      toast.error("Please enter valid shift timings");
    }
  };

  const openEditDetails = () => {
    setEditForm({
      name: data?.data?.name || "",
      phone: data?.data?.phone || "",
      address: data?.data?.address || "",
      is_shift_driver: data?.data?.is_shift_driver ?? false,
      registrationNumber: data?.vehicle?.registrationNumber || "",
      vehicle_type: data?.vehicle?.vehicle_type || "",
      vehicle_details: data?.vehicle?.vehicle_details || "",
    });
    toggleSidebar("editDetails", true);
  };

  const buildShiftSchedule = () => [
    { day: "Monday",    start_time: shiftTimings.MON[0] ? convertTo24Hour(shiftTimings.MON[0]) : "", end_time: shiftTimings.MON[1] ? convertTo24Hour(shiftTimings.MON[1]) : "" },
    { day: "Tuesday",   start_time: shiftTimings.TUE[0] ? convertTo24Hour(shiftTimings.TUE[0]) : "", end_time: shiftTimings.TUE[1] ? convertTo24Hour(shiftTimings.TUE[1]) : "" },
    { day: "Wednesday", start_time: shiftTimings.WED[0] ? convertTo24Hour(shiftTimings.WED[0]) : "", end_time: shiftTimings.WED[1] ? convertTo24Hour(shiftTimings.WED[1]) : "" },
    { day: "Thursday",  start_time: shiftTimings.THU[0] ? convertTo24Hour(shiftTimings.THU[0]) : "", end_time: shiftTimings.THU[1] ? convertTo24Hour(shiftTimings.THU[1]) : "" },
    { day: "Friday",    start_time: shiftTimings.FRI[0] ? convertTo24Hour(shiftTimings.FRI[0]) : "", end_time: shiftTimings.FRI[1] ? convertTo24Hour(shiftTimings.FRI[1]) : "" },
    { day: "Saturday",  start_time: shiftTimings.SAT[0] ? convertTo24Hour(shiftTimings.SAT[0]) : "", end_time: shiftTimings.SAT[1] ? convertTo24Hour(shiftTimings.SAT[1]) : "" },
    { day: "Sunday",    start_time: shiftTimings.SUN[0] ? convertTo24Hour(shiftTimings.SUN[0]) : "", end_time: shiftTimings.SUN[1] ? convertTo24Hour(shiftTimings.SUN[1]) : "" },
  ];

  const handleUpdateDriver = async () => {
    if (editForm.is_shift_driver) {
      const hasAtLeastOneShift = Object.values(shiftTimings).some(
        ([start, end]) => start !== "" && end !== ""
      );
      if (!hasAtLeastOneShift) {
        toast.error("Add shift timings for at least one day before saving.");
        return;
      }
      if (Object.keys(shiftErrors).length > 0) {
        toast.error("Please fix the shift timing errors first.");
        return;
      }
    }

    const payload = { driverId: id };
    if (editForm.name.trim()) payload.name = editForm.name.trim();
    if (editForm.phone.trim()) payload.phone = editForm.phone.trim();
    if (editForm.address.trim()) payload.address = editForm.address.trim();
    payload.is_shift_driver = editForm.is_shift_driver;
    const vehicle = {};
    if (editForm.registrationNumber.trim()) vehicle.registrationNumber = editForm.registrationNumber.trim();
    if (editForm.vehicle_type.trim()) vehicle.vehicle_type = editForm.vehicle_type.trim();
    if (editForm.vehicle_details.trim()) vehicle.vehicle_details = editForm.vehicle_details.trim();
    if (Object.keys(vehicle).length > 0) payload.vehicle = vehicle;

    setEditSaving(true);
    const driverResult = await dispatch(updateDriverService(payload));

    if (updateDriverService.fulfilled.match(driverResult) && editForm.is_shift_driver) {
      await dispatch(shiftScheduleService({ driverID: id, newSchedule: buildShiftSchedule() }));
    }

    setEditSaving(false);
    if (updateDriverService.fulfilled.match(driverResult)) {
      toast.success(driverResult?.payload?.message || "Driver updated successfully");
      setSidebars((prev) => ({ ...prev, editDetails: false }));
      mutate();
    }
  };

  const handleDriverAction = async (actionType) => {
    if (actionType === "suspendDriver") {
      setSuspendDriver((prevState) => ({
        ...prevState,
        [actionType]: true,
      }));
    } else if (actionType === "suspendDriverConfirm") {
      const resultAction = await dispatch(
        suspendDriverService({ driverID: id })
      );

      if (suspendDriverService.fulfilled.match(resultAction)) {
        toast.success(resultAction?.payload?.message);
        setSuspendDriver((prev) => ({
          ...prev,
          suspendDriverConfirm: true,
        }));
        mutate();
      }
    } else if (actionType === "enableSuspendedDriver") {
      const resultAction = await dispatch(
        enableDriverService({ driverID: id })
      );

      if (enableDriverService.fulfilled.match(resultAction)) {
        toast.success(resultAction?.payload?.message);
        setSuspendDriver({
          suspendDriver: false,
          suspendDriverConfirm: false,
          enableSuspendedDriver: false,
        });

        setSidebars({
          editPoints: false,
          shiftTimings: false,
          suspendDriver: false,
          cancelTrip: false,
          deleteDriver: false,
        });

        mutate();
      }
    } else if (actionType === "sendOtp") {
      if (!isLoading2) {
        const resultAction = await dispatch(otpService({ email: user?.email }));

        if (otpService.fulfilled.match(resultAction)) {
          toast.success(resultAction?.payload?.message);

          setDeleteDriver((prevState) => ({
            ...prevState,
            deleteDriver: true,
          }));
        }
      }
    } else if (actionType === "deleteDriver") {
      const resultAction = await dispatch(
        deleteDriverService({ driverID: id, otp: otp })
      );

      if (deleteDriverService.fulfilled.match(resultAction)) {
        toast.success(resultAction?.payload?.message);
        navigate("/drivers");
      }
    } else if (actionType === "updatePoints") {
      const resultAction = await dispatch(
        updatePointsService({
          driverId: id,
          points: parseInt(points),
        })
      );

      if (updatePointsService.fulfilled.match(resultAction)) {
        toast.success(resultAction?.payload?.message);
        mutate();

        setSidebars({
          cancelTrip: false,
          editPoints: false,
          shiftTimings: false,
          suspendDriver: false,
          deleteDriver: false,
        });
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !Object.keys(sidebarRefs).some((key) =>
          sidebarRefs[key]?.current?.contains(event.target)
        )
      ) {
        handleCloseSidebar();
      }
    };

    if (Object.values(sidebars).some((val) => val)) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [sidebars, shiftErrors]);

  const renderTable = () => (
    <div className="flex flex-col gap-5 w-full">
      <DriverDeliveryHistoryTable
        headers={deliveryHistoryHeader}
        data={recentOrders}
      />
    </div>
  );

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Driver Profiles & Performance Metrics"
        pageDescription="Enhance your delivery operations with Beep! Manage new driver requests, view delivery history, track driver performance, and monitor weekly earnings. Suspend or delete drivers effortlessly."
        pageKeywords="Beep, driver management, new driver requests, delivery history, driver performance, suspend driver, delete driver, weekly earnings"
      />
      <MobileResponsive />
      <div className="lg:flex hidden items-start">
        <Sidebar />

        {Object.values(sidebars).some((isOpen) => isOpen) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-30 transition-opacity duration-500 ease-in-out"></div>
        )}

        <div className="ml-[14.5vw] w-full h-screen overflow-y-auto px-10 py-8 text-secondary relative">
          <div className="flex w-full">
            <Link to="/drivers" className="flex items-center gap-3">
              <div className="bg-[#F75E00] w-5 h-5 flex items-center justify-center rounded-full pr-[1.5px]">
                <img
                  src="/assets/left-white-arrow.svg"
                  alt=""
                  className="w-[6px]"
                />
              </div>
              <span className="font-semibold text-base">Driver details</span>
            </Link>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-2 w-full h-[75dvh]">
              <div className="h-12 w-12 rounded-full border-4 border-t-quaternary border-b-quaternary border-r-transparent border-l-transparent animate-spin"></div>
              <p className="text-gray-600 font-medium">Loading..</p>
            </div>
          ) : (
            <div className="w-full flex items-start">
              <div className="w-[41%]">
                <div className="py-8 w-full">
                  <div className="flex items-center justify-between w-[84.5%]">
                    <h1 className="text-[21px] font-bold tracking-wide mb-2 capitalize">
                      {data?.data?.name}
                    </h1>
                    <p
                      className={`text-[13px] ${
                        data?.data?.is_verified
                          ? data?.data?.activeForOrders === "on" &&
                            !data?.data?.onBreak
                            ? "text-[#25AD34]"
                            : data?.data?.activeForOrders === "off" &&
                              data?.data?.onBreak
                            ? "text-[#F9C45D]"
                            : "text-[#F34B50]"
                          : "text-[#F34B50]"
                      } font-semibold tracking-wide flex items-center gap-0.5 h-1 2xl:mb-2.5 xl:mb-1.5 lg:mb-1 pt-1`}
                    >
                      <span className="2xl:pt-[3px] xl:pt-[3.5px] lg:pt-[3.5px]">
                        {data?.data?.is_verified
                          ? data?.data?.activeForOrders === "on" &&
                            !data?.data?.onBreak
                            ? "Online"
                            : data?.data?.activeForOrders === "off" &&
                              data?.data?.onBreak
                            ? "On a break"
                            : "Offline"
                          : "Not verified"}
                      </span>
                      <span className="text-3xl">•</span>
                    </p>
                  </div>

                  <div className="border-2 border-quaternary w-[84.5%] px-5 py-1.5 rounded-lg flex items-center justify-between bg-[#F7F9F8]">
                    {/* <img
                      src="/assets/user.png"
                      alt={data?.data?.name}
                      className="h-[5.3rem] w-[5.3rem] rounded-full"
                    /> */}

                    {data?.data?.driver_image ? (
                      data?.data?.driver_image?.includes("img.freepik.com") ? (
                        <Avatar
                          name={data?.data?.name}
                          size="84"
                          round={true}
                          maxInitials={2}
                          textSizeRatio={2.5}
                          style={{ letterSpacing: "1px" }}
                        />
                      ) : (
                        <img
                          src={data?.data?.driver_image}
                          alt=""
                          className="h-[84px] rounded-full"
                        />
                      )
                    ) : (
                      <Avatar
                        name={data?.data?.name}
                        size="84"
                        round={true}
                        maxInitials={2}
                        textSizeRatio={2.5}
                        style={{ letterSpacing: "1px" }}
                      />
                    )}

                    <div className="text-center">
                      <h1 className="text-3xl font-bold tracking-wide text-primary">
                        {data?.data?.points}
                      </h1>

                      <button
                        onClick={() => toggleSidebar("editPoints", true)}
                        className="text-[11px] font-bold tracking-wide text-primary"
                      >
                        <span className="capitalize">
                          {data?.data?.category}
                        </span>{" "}
                        tier
                      </button>
                    </div>
                  </div>

                  <h2 className="text-[17px] font-semibold pt-3 pb-2">
                    Driver details
                  </h2>

                  <div className="border-2 border-quaternary w-[84.5%] px-5 py-1.5 rounded-lg flex flex-col items-center justify-between bg-[#F7F9F8]">
                    {[
                      // { label: "Driver ID", value: data?.data?._id },
                      {
                        label: "Contact number",
                        value: data?.data?.phone ? data?.data?.phone : "N/A",
                      },
                      {
                        label: "Email",
                        value: data?.data?.email ? data?.data?.email : "N/A",
                      },
                      {
                        label: "Vehicle type",
                        value: data?.vehicle?.vehicle_type
                          ? data?.vehicle?.vehicle_type
                          : "N/A",
                      },
                      {
                        label: "Vehicle",
                        value: data?.vehicle?.vehicle_details
                          ? data?.vehicle?.vehicle_details
                          : "N/A",
                      },
                      {
                        label: "Registration No.",
                        value: data?.vehicle?.registrationNumber
                          ? data?.vehicle?.registrationNumber
                          : "N/A",
                      },
                      {
                        label: "Deliveries done",
                        value: data?.deliveredOrders || 0,
                      },
                      {
                        label: "Payout due",
                        value:
                          (data?.data?.payout_amount &&
                            `£${data?.data?.payout_amount}`) ||
                          0,
                      },
                      {
                        label: "Address",
                        value: data?.data?.address
                          ? data?.data?.address
                          : "N/A",
                      },
                      ...(data?.data?.is_shift_driver !== undefined
                        ? [
                            {
                              label: "Shift Driver",
                              value: data?.data?.is_shift_driver ? "Yes" : "No",
                            },
                          ]
                        : []),

                      // {
                      //   label: "Driver's licence",
                      //   value: data?.data?.driver_license
                      //     ? data?.data?.driver_license
                      //     : "N/A",
                      // },
                      // {
                      //   label: "National insurance",
                      //   value: data?.data?.driver_national_insurance
                      //     ? data?.data?.driver_national_insurance
                      //     : "N/A",
                      // },
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start w-full py-2">
                        <p className="2xl:text-[15px] xl:text-[13px] lg:text-[13px] font-semibold tracking-wide w-1/2 break-all">
                          {item.label}
                        </p>
                        <p
                          className={`2xl:text-[15px] xl:text-[13px] lg:text-[13px] font-semibold tracking-wide w-1/2 break-all ${
                            item.label === "Registration No." && "uppercase"
                          }`}
                        >
                          {item.value}
                        </p>
                      </div>
                    ))}
                  </div>

                  <h2 className="text-[17px] font-semibold pt-3 pb-2">
                    Driver location
                  </h2>

                  <div className="border-2 border-quaternary w-[84.5%] h-52 rounded-lg flex flex-col justify-center items-center bg-[#F7F9F8] overflow-hidden">
                    {isLoading ? (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                        <div className="w-full h-full bg-gray-200 rounded-lg animate-pulse"></div>
                      </div>
                    ) : (
                      <Map
                        mapId={"terrain"}
                        defaultZoom={6}
                        defaultCenter={{ lat: 54.5, lng: -3.0 }}
                        gestureHandling={"greedy"}
                        onClick={onMapClick}
                        disableDefaultUI
                        styles={"terrain"}
                        fullscreenControl={true}
                        mapTypeId={"terrain"}
                        mapTypeControlOptions
                        zoomControl
                        scaleControl
                        mapTypeControl
                        rotateControl
                      >
                        <MapPanner driverLocation={driverLocation} />
                        <MapMarker
                          data={mapDriver}
                          entityType="drivers"
                          onMouseEnter={handleMarkerClick}
                          useDriverIcon={true}
                        />

                        <InfoWindowPopup
                          infoWindowShown={infoWindowShown}
                          selectedMarker={selectedMarker}
                          handleCloseInfoWindow={handleCloseInfoWindow}
                        />
                      </Map>
                    )}
                  </div>

                  <div className="w-[84.5%] flex flex-col gap-3 py-5">
                    <div className="m-auto flex items-center gap-5 2xl:w-[80%] xl:w-[90%] lg:w-[90%]">
                      <TernaryButton
                        text="Weekly earnings"
                        path={`/drivers/weekly-earnings/${id}`}
                        uppercase={false}
                        mutation={true}
                        width="w-full"
                      />
                      <TernaryButton
                        text="Shift timings"
                        uppercase={false}
                        mutation={true}
                        width="w-full"
                        event={() => {
                          if (data?.data?.is_shift_driver === false) {
                            setNonShiftModal(true);
                          } else {
                            toggleSidebar("shiftTimings", true);
                          }
                        }}
                      />
                    </div>
                    <div className="m-auto 2xl:w-[80%] xl:w-[90%] lg:w-[90%]">
                      <TernaryButton
                        text="Edit details"
                        uppercase={false}
                        mutation={true}
                        width="w-full"
                        event={openEditDetails}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-[57%]">
                <div className="py-8 w-full">
                  <div className="flex justify-between items-center mb-2">
                    <h1 className="text-[21px] font-bold tracking-wide">
                      Delivery History
                    </h1>
                    <Link
                      to={`/history/drivers/${id}`}
                      className="text-[#F87638] text-[15px] font-bold tracking-wide flex items-center gap-1.5"
                    >
                      View more{" "}
                      <img
                        src="/assets/right-arrow-orange.svg"
                        alt=""
                        className="h-[11px] mt-0.5"
                      />
                    </Link>
                  </div>
                  <div className="w-full rounded-lg flex flex-col items-center justify-between gap-4">
                    {renderTable()}
                  </div>

                  <div className="w-full py-5">
                    <div className="m-auto flex items-center gap-5 w-[60%]">
                      <TernaryButton
                        text={
                          data?.data?.is_suspended
                            ? "Enable driver"
                            : "Suspend driver"
                        }
                        uppercase={false}
                        mutation={true}
                        width="w-full"
                        event={() => toggleSidebar("suspendDriver", true)}
                      />
                      <TernaryButton
                        text="Delete driver"
                        uppercase={false}
                        mutation={true}
                        width="w-full"
                        event={() => toggleSidebar("deleteDriver", true)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Details Sidebar */}
          <div
            ref={sidebarRefs.editDetails}
            className={`fixed right-0 top-0 h-full 2xl:w-[40%] xl:w-[43%] lg:w-[50%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.editDetails ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center gap-5">
              <button
                onClick={() => setSidebars((prev) => ({ ...prev, editDetails: false }))}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>

              <div className="flex w-[90%]">
                <div
                  onClick={() => setSidebars((prev) => ({ ...prev, editDetails: false }))}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <div className="bg-[#F75E00] w-6 h-6 flex items-center justify-center rounded-full">
                    <img src="/assets/left-white-arrow.svg" alt="" className="w-[7px]" />
                  </div>
                  <span className="font-semibold 2xl:text-[18px] xl:text-[18px] lg:text-[16px]">
                    Edit driver details
                  </span>
                </div>
              </div>

              <div className="w-[90%] flex flex-col gap-4">
                {/* Personal details */}
                <p className="text-sm font-bold text-gray-500 uppercase tracking-wide">Personal</p>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-semibold text-gray-600">Name</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                    className="border-2 border-quaternary rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-quaternary/30"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-semibold text-gray-600">Phone</label>
                  <input
                    type="text"
                    value={editForm.phone}
                    onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))}
                    className="border-2 border-quaternary rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-quaternary/30"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-semibold text-gray-600">Address</label>
                  <textarea
                    value={editForm.address}
                    onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))}
                    rows={2}
                    className="border-2 border-quaternary rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-quaternary/30 resize-none"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Shift driver</label>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {editForm.is_shift_driver ? "Yes — has fixed shift hours" : "No — flexible hours"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditForm((p) => ({ ...p, is_shift_driver: !p.is_shift_driver }))}
                    className={`relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none flex-shrink-0 ${
                      editForm.is_shift_driver ? "bg-quaternary" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                        editForm.is_shift_driver ? "translate-x-6" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>

                {/* Shift timings — visible only when shift driver is on */}
                {editForm.is_shift_driver && (
                  <div className="flex flex-col gap-3 border-2 border-quaternary rounded-xl p-4 bg-[#F7F9F8]">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-bold text-gray-700">Shift timings</p>
                      <p className="text-xs text-gray-400">At least one day required</p>
                    </div>
                    {Object.entries(shiftTimings).map(([day, times], dayIndex) => (
                      <div
                        key={dayIndex}
                        className={`flex justify-between items-center gap-2 ${
                          highlightedTimes[day] !== undefined ? "rounded-md" : ""
                        }`}
                      >
                        <span
                          className={`font-bold text-sm w-10 flex-shrink-0 ${
                            highlightedTimes[day] !== undefined ? "text-red-500" : "text-gray-800"
                          }`}
                        >
                          {day}
                        </span>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {times.map((time, index) => (
                            <TimeInput
                              key={index}
                              initialTime={time}
                              day={day}
                              index={index}
                              handleEditTime={handleEditTime}
                              isHighlighted={highlightedTimes[day] === index}
                              disabled={false}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                    {Object.keys(shiftErrors).length > 0 && (
                      <p className="text-xs text-red-500 mt-1">
                        {Object.values(shiftErrors)[0]}
                      </p>
                    )}
                    {!Object.values(shiftTimings).some(([s, e]) => s !== "" && e !== "") && (
                      <p className="text-xs text-amber-500">
                        Add at least one day with start and end times.
                      </p>
                    )}
                  </div>
                )}

                {/* Vehicle details */}
                <p className="text-sm font-bold text-gray-500 uppercase tracking-wide mt-2">Vehicle</p>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-semibold text-gray-600">Registration number</label>
                  <input
                    type="text"
                    value={editForm.registrationNumber}
                    onChange={(e) => setEditForm((p) => ({ ...p, registrationNumber: e.target.value }))}
                    className="border-2 border-quaternary rounded-lg px-4 py-2 text-sm uppercase outline-none focus:ring-2 focus:ring-quaternary/30"
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-semibold text-gray-600">Vehicle type</label>
                  <select
                    value={editForm.vehicle_type}
                    onChange={(e) => setEditForm((p) => ({ ...p, vehicle_type: e.target.value }))}
                    className="border-2 border-quaternary rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-quaternary/30 bg-white"
                  >
                    <option value="">Select type</option>
                    <option value="two-wheeler">Two-wheeler</option>
                    <option value="four-wheeler">Four-wheeler</option>
                    <option value="bicycle">Bicycle</option>
                    <option value="van">Van</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-semibold text-gray-600">Vehicle model</label>
                  <input
                    type="text"
                    value={editForm.vehicle_details}
                    onChange={(e) => setEditForm((p) => ({ ...p, vehicle_details: e.target.value }))}
                    className="border-2 border-quaternary rounded-lg px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-quaternary/30"
                  />
                </div>
              </div>

              <div className="w-[90%]">
                <PrimaryButton
                  text={editSaving ? "Saving…" : "Save changes"}
                  width="w-full"
                  uppercase={false}
                  disabled={editSaving}
                  event={handleUpdateDriver}
                />
              </div>
            </div>
          </div>

          {/* Non-shift driver modal */}
          {nonShiftModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
              <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-xl border-t-4 border-quaternary">
                <h2 className="text-lg font-bold text-gray-800 mb-3">Cannot Edit Shift</h2>
                <p className="text-gray-600 mb-6">
                  <strong className="capitalize">{data?.data?.name}</strong> is a non-shift driver. To edit their shift timings, first make them a shift driver.
                </p>
                <button
                  onClick={() => setNonShiftModal(false)}
                  className="w-full bg-quaternary text-white font-semibold py-2.5 rounded-lg hover:bg-orange-600 transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          )}

          {/* Shift Timings Sidebar */}
          <div
            ref={sidebarRefs.shiftTimings}
            className={`fixed right-0 top-0 h-full 2xl:w-[40%] xl:w-[43%] lg:w-[50%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.shiftTimings ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>

              <div className="flex w-[90%] pb-10">
                <div
                  onClick={handleCloseSidebar}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <div className="bg-[#F75E00] w-6 h-6 flex items-center justify-center rounded-full">
                    <img
                      src="/assets/left-white-arrow.svg"
                      alt=""
                      className="w-[7px]"
                    />
                  </div>
                  <span className="font-semibold 2xl:text-[18px] xl:text-[18px] lg:text-[16px]">
                    Edit shift timings
                  </span>
                </div>
              </div>

              <div className="border-2 border-quaternary w-[90%] rounded-lg flex flex-col items-center justify-between gap-4 bg-[#F7F9F8]">
                <div className="flex flex-col gap-5 py-5 w-full">
                  {Object.entries(shiftTimings).map(
                    ([day, times], dayIndex) => (
                      <div
                        key={dayIndex}
                        className={`flex justify-between items-center gap-2 w-full px-4 ${
                          highlightedTimes[day] !== undefined ? "rounded-md" : ""
                        }`}
                      >
                        <span
                          className={`font-bold 2xl:text-base xl:text-base lg:text-sm ${
                            highlightedTimes[day] !== undefined
                              ? "text-[#E00000]"
                              : "text-black"
                          }`}
                        >
                          {day}
                        </span>

                        <div className="flex justify-end items-center gap-2 flex-1 min-w-0">
                          {times[0] === "" && times[1] === "" && disabledST ? (
                            <div className="flex justify-center items-center w-full">
                              <span>No shift</span>
                            </div>
                          ) : (
                            times.map((time, index) => (
                              <TimeInput
                                key={index}
                                initialTime={time}
                                day={day}
                                index={index}
                                handleEditTime={handleEditTime}
                                isHighlighted={highlightedTimes[day] === index}
                                disabled={disabledST}
                              />
                            ))
                          )}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              <div className="w-[90%] m-auto flex justify-center py-5">
                <PrimaryButton
                  text={disabledST ? "Edit Shift" : "Confirm Shift"}
                  width="w-full"
                  uppercase={false}
                  event={handleConfirmShift}
                  disabled={Object.keys(shiftErrors).length > 0}
                />
              </div>
            </div>
          </div>

          {/* Suspend Driver Sidebar */}
          <div
            ref={sidebarRefs.suspendDriver}
            className={`fixed right-0 top-0 h-full 2xl:w-[40%] xl:w-[43%] lg:w-[50%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.suspendDriver ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>

              <div className="flex w-[90%] pb-10">
                <div
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={cancelTrip ? () => setCancelTrip(false) : undefined}
                >
                  <div className="bg-[#F75E00] w-6 h-6 flex items-center justify-center rounded-full">
                    <img
                      src="/assets/left-white-arrow.svg"
                      alt=""
                      className="w-[7px]"
                    />
                  </div>
                  <span className="font-semibold 2xl:text-[25px] xl:text-[24px] lg:text-[23px]">
                    {!data?.data?.is_suspended
                      ? "Suspend driver"
                      : "Enable driver"}
                  </span>
                </div>
              </div>

              <div className="border-2 border-quaternary w-[90%] mb-5 px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                {/* <img
                  src="/assets/user.png"
                  alt={data?.data?.name}
                  className="h-[5.3rem] w-[5.3rem] rounded-full"
                /> */}
                <Avatar
                  name={data?.data?.name}
                  size="75"
                  round={true}
                  maxInitials={2}
                  textSizeRatio={2.5}
                  style={{ letterSpacing: "1px" }}
                />
                <div className="text-center">
                  <h1 className="text-xl font-bold tracking-wide capitalize">
                    {data?.data?.name}
                  </h1>
                </div>
              </div>

              <div className="w-[90%] mb-5 px-5 py-3 rounded-lg flex justify-between items-center shadow-lg">
                <p className="2xl:text-[18px] xl:text-[18px] lg:text-[16px] font-semibold w-full">
                  {data?.data?.phone}
                </p>
                <div className="w-full flex justify-end items-center">
                  <PrimaryButton
                    text={"Call driver"}
                    path={`tel:${data?.data?.phone}`}
                    width="w-[60%]"
                    uppercase={false}
                  />
                </div>
              </div>

              <div className="w-[90%] px-[4px] py-5 rounded-lg">
                <p className="2xl:text-lg xl:text-lg lg:text-[16px]">
                  {!suspendDriver.suspendDriver && !data?.data?.is_suspended ? (
                    <>
                      The driver <strong> {data?.data?.name}</strong> will not
                      be able to accept orders and request deliveries but will
                      be able to access their account.
                      {/* This account will be suspended for
                      <strong> one week</strong> due to ongoing order dispatch
                      problems and multiple driver reports. We value your
                      cooperation in restoring operational efficiency. */}
                    </>
                  ) : !suspendDriver.suspendDriverConfirm &&
                    !data?.data?.is_suspended ? (
                    <>
                      This account will be suspended until you{" "}
                      <strong>activate</strong> it. Are you sure you want to
                      suspend {data?.data?.name}'s account?
                    </>
                  ) : (
                    <>
                      This account is suspended successfully. Are you sure you
                      want to <strong> enable</strong> {data?.data?.name}'s
                      account?
                    </>
                  )}
                </p>
              </div>

              <div className="w-[90%] m-auto flex flex-col justify-center gap-5 py-5">
                <PrimaryButton
                  text={
                    !suspendDriver.suspendDriver && !data?.data?.is_suspended
                      ? "Suspend driver"
                      : !suspendDriver.suspendDriverConfirm &&
                        !data?.data?.is_suspended
                      ? "Suspend driver"
                      : "Enable driver"
                  }
                  width="w-full"
                  uppercase={false}
                  event={
                    !data?.data?.is_suspended
                      ? suspendDriver.suspendDriver
                        ? suspendDriver.suspendDriverConfirm
                          ? suspendDriver.enableSuspendedDriver
                            ? null
                            : () => handleDriverAction("enableSuspendedDriver")
                          : () => handleDriverAction("suspendDriverConfirm")
                        : () => handleDriverAction("suspendDriver")
                      : () => handleDriverAction("enableSuspendedDriver")
                  }
                />
              </div>
            </div>
          </div>

          {/* Edit Driver's Points Sidebar */}
          <div
            ref={sidebarRefs.editPoints}
            className={`fixed right-0 top-0 h-full w-[40%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.editPoints ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>

              <div className="flex w-[90%] pb-10">
                <div className="flex items-center gap-3 cursor-pointer">
                  <div className="bg-[#F75E00] w-6 h-6 flex items-center justify-center rounded-full">
                    <img
                      src="/assets/left-white-arrow.svg"
                      alt=""
                      className="w-[7px]"
                    />
                  </div>
                  <span className="font-semibold 2xl:text-[18px] xl:text-[18px] lg:text-[16px]">
                    Edit driver’s points
                  </span>
                </div>
              </div>

              <div className="border-2 border-quaternary w-[90%] mb-5 px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                {/* <img
                  src="/assets/user.png"
                  alt={data?.data?.name}
                  className="h-[5.3rem] w-[5.3rem] rounded-full"
                /> */}
                <Avatar
                  name={data?.data?.name}
                  size="75"
                  round={true}
                  maxInitials={2}
                  textSizeRatio={2.5}
                  style={{ letterSpacing: "1px" }}
                />
                <div className="text-center">
                  <h1 className="text-xl font-bold tracking-wide capitalize">
                    {data?.data?.name}
                  </h1>
                </div>
              </div>

              <div className="w-[90%] mb-5 px-5 py-3 rounded-lg flex justify-between items-center shadow-lg">
                <p className="text-[18px] font-semibold w-full">
                  {data?.data?.phone}
                </p>
                <div className="w-full flex justify-end items-center">
                  <PrimaryButton
                    text={"Call driver"}
                    path={`tel:${data?.data?.phone}`}
                    width="w-[60%]"
                    uppercase={false}
                  />
                </div>
              </div>

              <div className="w-[90%] px-[4px] py-5 rounded-lg">
                <p className="text-lg">Edit the driver’s points here</p>
              </div>

              <div className="flex justify-between items-center space-x-4 w-[90%] px-[4px] py-5 rounded-lg">
                {/* Subtract 10 Points Button */}
                <div className="flex items-center gap-3">
                  <label htmlFor="Subtract 10 points">Subtract 10 points</label>
                </div>
                <div className="flex items-center gap-5">
                  <button
                    className="w-7 h-7 flex justify-center items-center rounded-full bg-[#A7A7A7] text-white hover:bg-[#b8b8b8] pb-1"
                    onClick={handleDecrement}
                  >
                    –
                  </button>

                  {/* Points Display */}
                  <input
                    type="number"
                    value={points}
                    min="0"
                    onChange={(e) => setPoints(e.target.value)}
                    className="w-24 h-12 border-2 border-orange-400 rounded-md text-2xl text-orange-500 font-bold text-center"
                  />

                  <button
                    className="w-7 h-7 flex justify-center items-center rounded-full bg-orange-500 text-white hover:bg-orange-600"
                    onClick={handleIncrement}
                  >
                    &#x271B;
                  </button>
                </div>

                {/* Add 10 Points Button */}
                <div className="flex items-center text-end gap-3">
                  <label htmlFor="Add 10 points">Add 10 points</label>
                </div>
              </div>

              <div className="w-[90%] m-auto flex flex-col justify-center gap-5 py-5">
                <PrimaryButton
                  text={"Submit"}
                  width="w-full"
                  uppercase={false}
                  event={() => handleDriverAction("updatePoints")}
                />
              </div>
            </div>
          </div>

          {/* Delete Driver Sidebar */}
          <div
            ref={sidebarRefs.deleteDriver}
            className={`fixed right-0 top-0 h-full w-[40%] bg-white shadow-lg overflow-y-auto transform transition-transform duration-[500ms] ease-in-out z-40 border-t-4 border-primary ${
              sidebars.deleteDriver ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="p-8 relative flex flex-col items-center">
              <button
                onClick={handleCloseSidebar}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 scale-150"
              >
                &times;
              </button>
              <div className="flex w-[90%] pb-10">
                <div className="flex items-center gap-3 cursor-pointer">
                  <div className="bg-[#F75E00] w-6 h-6 flex items-center justify-center rounded-full">
                    <img
                      src="/assets/left-white-arrow.svg"
                      alt=""
                      className="w-[7px]"
                    />
                  </div>
                  <span className="font-semibold text-[25px]">
                    Delete driver
                  </span>
                </div>
              </div>

              <div className="border-2 border-quaternary w-[90%] mb-5 px-5 py-1.5 rounded-lg flex items-center gap-3 bg-[#F7F9F8]">
                {/* <img
                  src="/assets/user.png"
                  alt={data?.data?.name}
                  className="h-[5.3rem] w-[5.3rem] rounded-full"
                /> */}
                <Avatar
                  name={data?.data?.name}
                  size="75"
                  round={true}
                  maxInitials={2}
                  textSizeRatio={2.5}
                  style={{ letterSpacing: "1px" }}
                />
                <div className="text-center">
                  <h1 className="text-xl font-bold tracking-wide capitalize">
                    {data?.data?.name}
                  </h1>
                </div>
              </div>

              <div className="w-[90%] mb-5 px-5 py-3 rounded-lg flex justify-between items-center shadow-lg">
                <p className="2xl:text-[18px] xl:text-[18px] lg:text-[16px] font-semibold w-full">
                  {data?.data?.phone}
                </p>
                <div className="w-full flex justify-end items-center">
                  <PrimaryButton
                    text={"Call driver"}
                    path={`tel:${data?.data?.phone}`}
                    width="w-[60%]"
                    uppercase={false}
                  />
                </div>
              </div>

              <div className="w-[90%] px-[4px] py-5 rounded-lg">
                <div className="2xl:text-lg xl:text-lg lg:text-[16px]">
                  {!deleteDriver.deleteDriver ? (
                    <>
                      <p>
                        The driver{" "}
                        <strong className="capitalize">
                          {data?.data?.name}
                        </strong>{" "}
                        will be permanently deleted from the database and will
                        loose access to their account.
                        {/* This account is scheduled forx
                        permanent deletion. If you wish to proceed with deleting
                        the account <strong>{data?.data?.name}</strong>, please
                        request a one-time password (OTP) for confirmation. */}
                      </p>

                      <div className="w-[90%] m-auto flex flex-col justify-center gap-5 py-10">
                        <PrimaryButton
                          text="Request a OTP"
                          width="w-full"
                          uppercase={false}
                          event={() => handleDriverAction("sendOtp")}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <form
                        action=""
                        className="w-full"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <div className="py-5 flex flex-col gap-5">
                          <label
                            htmlFor="OTP"
                            className="2xl:text-[17px] xl:text-[15px] lg:text-[14px] font-semibold text-[#413F3F]"
                          >
                            Please enter the OTP sent to your email address
                          </label>
                          <FloatingInput
                            name="otp"
                            id="otp"
                            type="number"
                            placeholder="Enter OTP"
                            value={otp}
                            error={errors.otp}
                            handleBlur={handleOtpBlur}
                            onChange={(e) => setOtp(e.target.value)}
                          />
                        </div>

                        <div className="w-full m-auto flex flex-col justify-center gap-5 pt-5">
                          <PrimaryButton
                            text="Confirm"
                            width="w-full"
                            type="submit"
                            uppercase={false}
                            event={() => handleDriverAction("deleteDriver")}
                          />
                        </div>
                      </form>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DriverDetail;
